import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    DialogContentText,
    LinearProgress
} from '@mui/material';
import { notify } from '../notifications';

type BlockAction = 'block' | 'unlock';

const Dangerous = () => {
    const [open, setOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [totalTables, setTotalTables] = useState(0);
    const [currentTable, setCurrentTable] = useState('');
    const [blockAction, setBlockAction] = useState<BlockAction>('block');

    const [isReindexing, setIsReindexing] = useState(false);
    const [reindexProgress, setReindexProgress] = useState(0);
    const [reindexCurrentTable, setReindexCurrentTable] = useState('');
    const [reindexDialogOpen, setReindexDialogOpen] = useState(false);

    useEffect(() => {
        document.title = 'Опасные функции | Zerde';
    }, []);

    const handleClickOpen = () => {
        setConfirmOpen(true);
    };

    const handlePasswordDialogOpen = () => {
        setConfirmOpen(false);
        setOpen(true);
        setPassword('');
        setError('');
    };

    const handleClose = () => {
        if (!isDeleting) {
            setOpen(false);
            setConfirmOpen(false);
        }
    };

    const handleBlockDialogOpen = (action: BlockAction) => {
        setBlockAction(action);
        setBlockConfirmOpen(true);
        setPassword('');
        setError('');
    };

    const handleBlockUsers = async () => {
        try {
            const username = localStorage.getItem('username');
            if (!username) {
                setError('Пользователь не авторизован');
                return;
            }

            setIsProcessing(true);

            const response = await fetch('https://tbl.zerdegroup.kz/api/toggle-users-block', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username,
                    password,
                    action: blockAction
                }),
            });

            const data = await response.json();

            if (data.success) {
                notify(data.message, 'success');
                setBlockConfirmOpen(false);
            } else {
                setError(data.message || 'Произошла ошибка');
                notify(data.message || 'Произошла ошибка', 'error');
            }
        } catch (err) {
            setError('Произошла ошибка при выполнении операции');
            notify('Произошла ошибка при выполнении операции', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleTruncateTables = async () => {
        try {
            const username = localStorage.getItem('username');
            if (!username) {
                setError('Пользователь не авторизован');
                return;
            }

            setIsDeleting(true);
            setProgress(0);
            handleClose();

            const response = await fetch('https://tbl.zerdegroup.kz/api/truncate-tables', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username,
                    password
                }),
            });

            const data = await response.json();

            if (data.success) {
                setTotalTables(data.totalTables);

                const eventSource = new EventSource('https://tbl.zerdegroup.kz/api/deletion-progress');

                eventSource.onmessage = (event) => {
                    const progressData = JSON.parse(event.data);
                    setProgress(progressData.progress);
                    setCurrentTable(progressData.currentTable);

                    if (progressData.progress === 100) {
                        eventSource.close();
                        setIsDeleting(false);
                        notify('Операция успешно завершена', 'success');
                    }
                };

                eventSource.onerror = () => {
                    eventSource.close();
                    setIsDeleting(false);
                    notify('Соединение потеряно. Проверьте статус операции', 'warning');
                };

            } else {
                setIsDeleting(false);
                setError(data.message || 'Неверный пароль');
                notify(data.message || 'Произошла ошибка', 'error');
            }
        } catch (err) {
            setIsDeleting(false);
            setError('Произошла ошибка при выполнении операции');
            notify('Произошла ошибка при выполнении операции', 'error');
        }
    };

    const handleReindexing = async () => {
        try {
            const username = localStorage.getItem('username');
            if (!username) {
                setError('Пользователь не авторизован');
                return;
            }

            setIsReindexing(true);
            setReindexProgress(0);
            setReindexDialogOpen(false);

            const response = await fetch('https://tbl.zerdegroup.kz/api/reindex-tables', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username,
                    password
                }),
            });

            const data = await response.json();

            if (data.success) {
                const eventSource = new EventSource('https://tbl.zerdegroup.kz/api/reindexing-progress');

                eventSource.onmessage = (event) => {
                    const progressData = JSON.parse(event.data);
                    setReindexProgress(progressData.progress);
                    setReindexCurrentTable(progressData.currentTable);

                    if (progressData.progress === 100) {
                        eventSource.close();
                        setIsReindexing(false);
                        notify('Переинлексация таблиц успешно завершена', 'success');
                    }
                };

                eventSource.onerror = () => {
                    eventSource.close();
                    setIsReindexing(false);
                    notify('Соединение потеряно. Проверьте статус операции', 'warning');
                };

            } else {
                setIsReindexing(false);
                setError(data.message || 'Неверный пароль');
                notify(data.message || 'Произошла ошибка', 'error');
            }
        } catch (err) {
            setIsReindexing(false);
            setError('Произошла ошибка при выполнении операции');
            notify('Произошла ошибка при выполнении операции', 'error');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleClickOpen}
                    disabled={isDeleting || isProcessing}
                >
                    Delete all data table
                </Button>

                {isDeleting && (
                    <Box sx={{ width: '70%' }}>
                        <Typography variant="body1">
                            Progress: {Math.round(progress)}%
                        </Typography>
                        {currentTable && (
                            <Typography variant="body2" color="text.secondary">
                                Current table: {currentTable}
                            </Typography>
                        )}
                        <Box sx={{ width: '100%', mt: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{ height: 10, borderRadius: 5 }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    color="warning"
                    onClick={() => handleBlockDialogOpen('block')}
                    disabled={isDeleting || isProcessing}
                >
                    Block all users
                </Button>

                <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleBlockDialogOpen('unlock')}
                    disabled={isDeleting || isProcessing}
                >
                    Unlock all users
                </Button>

            </Box>

            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                    variant="contained"
                    color="info"
                    onClick={() => setReindexDialogOpen(true)}
                    disabled={isDeleting || isProcessing || isReindexing}
                >
                    Re-Indexing
                </Button>

                {isReindexing && (
                    <Box sx={{ width: '70%', mt: 2 }}>
                        <Typography variant="body1">
                            Переиндексация: {Math.round(reindexProgress)}%
                        </Typography>
                        {reindexCurrentTable && (
                            <Typography variant="body2" color="text.secondary">
                                Текущая таблица: {reindexCurrentTable}
                            </Typography>
                        )}
                        <Box sx={{ width: '100%', mt: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={reindexProgress}
                                sx={{ height: 10, borderRadius: 5 }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            <Box sx={{mt: 2, }}>
                <Button
                variant='contained'
                color='secondary'
                disabled>
                    Global recalc
                </Button>
            </Box>

            <Dialog open={reindexDialogOpen} onClose={() => !isReindexing && setReindexDialogOpen(false)}>
                <DialogTitle>Переиндексация таблиц</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Вы собираетесь переиндексировать все таблицы.
                        Для подтверждения введите пароль администратора
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Пароль"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!error}
                        helperText={error}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => setReindexDialogOpen(false)}
                        disabled={isReindexing}
                    >
                        Отмена
                    </Button>
                    <Button
                        onClick={handleReindexing}
                        color="info"
                        variant="contained"
                        disabled={isReindexing}
                    >
                        Переиндексация
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmOpen} onClose={handleClose}>
                <DialogTitle>Подтверждение действия</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Вы собираетесь удалить все данные из таблиц пользователей.
                        Это действие необратимо. Вы уверены, что хотите продолжить?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={handleClose} color="primary">
                        Отмена
                    </Button>
                    <Button variant="contained" onClick={handlePasswordDialogOpen} color="error">
                        Продолжить
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Введите пароль</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Для подтверждения операции введите ваш пароль администратора
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Пароль"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!error}
                        helperText={error}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={handleClose}>Отмена</Button>
                    <Button
                        onClick={handleTruncateTables}
                        color="error"
                        variant="contained"
                        disabled={isDeleting}
                    >
                        Подтвердить удаление
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={blockConfirmOpen} onClose={() => !isProcessing && setBlockConfirmOpen(false)}>
                <DialogTitle>
                    {blockAction === 'block' ? 'Блокировка пользователей' : 'Разблокировка пользователей'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {blockAction === 'block'
                            ? 'Вы собираетесь заблокировать всех пользователей.'
                            : 'Вы собираетесь разблокировать всех пользователей.'}
                        Для подтверждения введите пароль администратора
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Пароль"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!error}
                        helperText={error}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => setBlockConfirmOpen(false)}
                        disabled={isProcessing}
                    >
                        Отмена
                    </Button>
                    <Button
                        onClick={handleBlockUsers}
                        color={blockAction === 'block' ? 'warning' : 'success'}
                        variant="contained"
                        disabled={isProcessing}
                    >
                        {blockAction === 'block' ? 'Заблокировать' : 'Разблокировать'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Dangerous;